import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShield, faGavel, faWrench, faHeadset } from '@fortawesome/free-solid-svg-icons';

const StaffContainer = styled.div`
  padding: 4rem 8rem;
  max-width: 1800px;
  margin: 0 auto;
  
  @media (max-width: 1200px) {
    padding: 3rem 4rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const StaffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StaffSection = styled.section`
  margin-bottom: 4rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const StaffCard = styled.div`
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    border-color: var(--primary-color);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 480px) {
    margin: 0 auto;
  }
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 51, 51, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  color: #aaa;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
  position: relative;
  padding-bottom: 2rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: var(--primary-color);
    box-shadow: 0 0 10px var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  color: #fff;
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  position: relative;
  display: inline-block;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 50px;
    height: 2px;
    background: linear-gradient(90deg, var(--primary-color), transparent);
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const StaffHeader = styled.div`
  background: linear-gradient(360deg, rgba(219, 7, 7, 0.95), var(--primary-color));
  padding: 1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at top right, rgba(255, 51, 51, 0.1), transparent 70%);
    pointer-events: none;
  }
`;

const StaffAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 1rem;
  overflow: hidden;
  border: 3px solid #fff;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

const StaffName = styled.h3`
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  margin: 0;
  font-size: 1.1rem;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const StaffRole = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const StaffContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const StaffBio = styled.p`
  color: #aaa;
  font-family: 'Poppins', sans-serif;
  margin-bottom: 1rem;
  line-height: 1.6;
  font-size: 0.9rem;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const StaffJoinDate = styled.p`
  color: #777;
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-style: italic;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const Staff = () => {
  const staffMembers = {
    owners: [
      {
        name: "FireLord",
        role: "Owner & Founder",
        avatar: "https://mc-heads.net/avatar/MHF_Herobrine",
        bio: "Founded LAVA server in 2020. Passionate about creating the best Minecraft experience for all players.",
        joinDate: "January 2020"
      }
    ],
    admins: [
      {
        name: "BlazeMaster",
        role: "Head Administrator",
        avatar: "https://mc-heads.net/avatar/MHF_Steve",
        bio: "Oversees all server operations and staff management. Contact for serious issues only.",
        joinDate: "February 2020"
      },
      {
        name: "LavaQueen",
        role: "Administrator",
        avatar: "https://mc-heads.net/avatar/MHF_Alex",
        bio: "Manages community events and server development. Always happy to help!",
        joinDate: "March 2020"
      }
    ],
    moderators: [
      {
        name: "EmberGuard",
        role: "Senior Moderator",
        avatar: "https://mc-heads.net/avatar/MHF_Blaze",
        bio: "Ensures server rules are followed and helps resolve player disputes.",
        joinDate: "April 2020"
      },
      {
        name: "FlameWatcher",
        role: "Moderator",
        avatar: "https://mc-heads.net/avatar/MHF_Wither",
        bio: "Monitors chat and gameplay to maintain a friendly environment for all players.",
        joinDate: "May 2020"
      },
      {
        name: "AshKeeper",
        role: "Moderator",
        avatar: "https://mc-heads.net/avatar/MHF_Enderman",
        bio: "Specializes in handling player reports and investigating rule violations.",
        joinDate: "June 2020"
      }
    ],
    helpers: [
      {
        name: "SparkHelper",
        role: "Helper",
        avatar: "https://mc-heads.net/avatar/MHF_Slime",
        bio: "Assists players with basic questions and server navigation.",
        joinDate: "August 2020"
      },
      {
        name: "CinderGuide",
        role: "Helper",
        avatar: "https://mc-heads.net/avatar/MHF_Villager",
        bio: "Specializes in helping new players learn server features and commands.",
        joinDate: "September 2020"
      },
      {
        name: "BurnSupport",
        role: "Helper",
        avatar: "https://mc-heads.net/avatar/MHF_Zombie",
        bio: "Provides technical support and helps with connection issues.",
        joinDate: "October 2020"
      },
      {
        name: "InfernoAssist",
        role: "Helper",
        avatar: "https://mc-heads.net/avatar/MHF_Skeleton",
        bio: "Focuses on community building and making everyone feel welcome.",
        joinDate: "November 2020"
      },
      {
        name: "PixelGuardian",
        role: "Helper",
        avatar: "https://mc-heads.net/avatar/MHF_Guardian",
        bio: "Keeps the server safe and assists new players with a smile.",
        joinDate: "January 2021"
      },
      {
        name: "CreeperBuddy",
        role: "Helper",
        avatar: "https://mc-heads.net/avatar/MHF_Creeper",
        bio: "Explosive in energy but always calm in helping players.",
        joinDate: "July 2021"
      }
    ]
  };

  return (
    <StaffContainer>
      <PageTitle>OUR STAFF TEAM</PageTitle>
      <PageSubtitle>
        Meet the dedicated team behind our server. Our staff members work tirelessly to ensure a safe, fun, and engaging environment for all players. Feel free to reach out to them if you need any assistance!
      </PageSubtitle>
      
      <StaffSection>
        <SectionTitle>
          <FontAwesomeIcon icon={faShield} style={{ marginRight: '10px' }} />
          Owners
        </SectionTitle>
        <StaffGrid>
          {staffMembers.owners.map((member, index) => (
            <StaffCard key={index}>
              <StaffHeader>
                <StaffAvatar>
                  <img src={member.avatar} alt={member.name} />
                </StaffAvatar>
                <StaffName>{member.name}</StaffName>
                <StaffRole>{member.role}</StaffRole>
              </StaffHeader>
              <StaffContent>
                <StaffBio>{member.bio}</StaffBio>
                <StaffJoinDate>Joined: {member.joinDate}</StaffJoinDate>
              </StaffContent>
            </StaffCard>
          ))}
        </StaffGrid>
      </StaffSection>
      
      <StaffSection>
        <SectionTitle>
          <FontAwesomeIcon icon={faGavel} style={{ marginRight: '10px' }} />
          Administrators
        </SectionTitle>
        <StaffGrid>
          {staffMembers.admins.map((member, index) => (
            <StaffCard key={index}>
              <StaffHeader>
                <StaffAvatar>
                  <img src={member.avatar} alt={member.name} />
                </StaffAvatar>
                <StaffName>{member.name}</StaffName>
                <StaffRole>{member.role}</StaffRole>
              </StaffHeader>
              <StaffContent>
                <StaffBio>{member.bio}</StaffBio>
                <StaffJoinDate>Joined: {member.joinDate}</StaffJoinDate>
              </StaffContent>
            </StaffCard>
          ))}
        </StaffGrid>
      </StaffSection>
      
      <StaffSection>
        <SectionTitle>
          <FontAwesomeIcon icon={faWrench} style={{ marginRight: '10px' }} />
          Moderators
        </SectionTitle>
        <StaffGrid>
          {staffMembers.moderators.map((member, index) => (
            <StaffCard key={index}>
              <StaffHeader>
                <StaffAvatar>
                  <img src={member.avatar} alt={member.name} />
                </StaffAvatar>
                <StaffName>{member.name}</StaffName>
                <StaffRole>{member.role}</StaffRole>
              </StaffHeader>
              <StaffContent>
                <StaffBio>{member.bio}</StaffBio>
                <StaffJoinDate>Joined: {member.joinDate}</StaffJoinDate>
              </StaffContent>
            </StaffCard>
          ))}
        </StaffGrid>
      </StaffSection>
      
      <StaffSection>
        <SectionTitle>
          <FontAwesomeIcon icon={faHeadset} style={{ marginRight: '10px' }} />
          Helpers
        </SectionTitle>
        <StaffGrid>
          {staffMembers.helpers.map((member, index) => (
            <StaffCard key={index}>
              <StaffHeader>
                <StaffAvatar>
                  <img src={member.avatar} alt={member.name} />
                </StaffAvatar>
                <StaffName>{member.name}</StaffName>
                <StaffRole>{member.role}</StaffRole>
              </StaffHeader>
              <StaffContent>
                <StaffBio>{member.bio}</StaffBio>
                <StaffJoinDate>Joined: {member.joinDate}</StaffJoinDate>
              </StaffContent>
            </StaffCard>
          ))}
        </StaffGrid>
      </StaffSection>
    </StaffContainer>
  );
};

export default Staff;
